import { roleAtLeast, UserRole } from '@atlas/types'
import { useMemo, useState } from 'react'
import { optionLabel } from '@/components/classification/ClassifyPicker'
import type { Article } from '@/domain/article.types'
import { useArticleTranslations, useRequestArticleTranslation } from '@/hooks/useArticles'
import { useAuth } from '@/hooks/useAuth'
import { useAxesWithValues } from '@/hooks/useLibrary'
import { apiErrorMessage } from '@/lib/api-error'
import { buildLocaleLabels, localeIdentityKey } from '@/lib/locale-identity'

/** The registry axis the target locales come from — never a hard-coded list. */
const LOCALE_AXIS_CODE = 'locale'

/** Immutable toggle on a locale-code set. */
function toggleCode(selected: ReadonlySet<string>, code: string): Set<string> {
	const next = new Set(selected)
	if (next.has(code)) next.delete(code)
	else next.add(code)
	return next
}

/**
 * Data and handlers for the article edit page's translation surfaces — the
 * locale strip and the translate dialog.
 *
 * Split out of the page for the same reason the message flow's panel hook was:
 * to keep `ArticleEditor` inside the 100-line function limit, not because it is
 * reused. No reset effect is needed for the selection: `EditArticlePage` keys
 * `ArticleEditor` on the article's id, so navigating between two variants
 * remounts this hook rather than feeding it a new article.
 */
export function useArticleTranslationPanel(article: Article) {
	const { user } = useAuth()
	const { data: variants = [], isLoading } = useArticleTranslations(article.articleId)
	const { axes } = useAxesWithValues()
	const request = useRequestArticleTranslation()
	const [selected, setSelected] = useState<ReadonlySet<string>>(new Set())
	const [isDialogOpen, setDialogOpen] = useState(false)

	// Labelled through the same helper the classification picker uses, so a locale
	// reads identically here and in every other axis surface.
	const localeOptions = useMemo(() => {
		const axis = axes.find((a) => a.code === LOCALE_AXIS_CODE)
		return (axis?.options ?? []).map((o) => ({ code: o.code, label: optionLabel(o.code, o.label) }))
	}, [axes])

	const labels = useMemo(() => buildLocaleLabels(localeOptions), [localeOptions])

	// Compared by identity key, not by string: the axis writes `us_en` while the
	// article defaults write `en-us`, and the endpoint's own-locale guard compares
	// exactly — so offering the axis's spelling of a locale the group already has
	// would create a second variant for it.
	const offerableLocales = useMemo(() => {
		if (isLoading) return [] // an unloaded group makes every locale look absent from it
		const covered = new Set(variants.map((v) => localeIdentityKey(v.languageIso)))
		return localeOptions.filter((o) => !covered.has(localeIdentityKey(o.code)))
	}, [isLoading, variants, localeOptions])

	const groupSource = variants.find((v) => v.isSource)
	// Mirrors the endpoint's own gates: requesting a translation is authoring work,
	// and a request must target the group's source. No further client-side matrix.
	const canRequest = article.isSource && roleAtLeast(user?.role ?? '', UserRole.USER)

	const submit = () => {
		request
			.mutateAsync({ articleId: article.articleId, targetLocales: [...selected] })
			.then(() => {
				setSelected(new Set())
				setDialogOpen(false)
			})
			// Swallowed here because the dialog renders `request.error`: rethrowing
			// would surface an unhandled rejection for a failure already on screen.
			.catch(() => undefined)
	}

	return {
		variants,
		isLoading,
		labels,
		offerableLocales,
		existingVariants: variants.filter((v) => !v.isSource),
		/** Given only when this variant is NOT the source, so the strip can say where to go instead. */
		source: article.isSource ? undefined : groupSource,
		onManage: canRequest ? () => setDialogOpen(true) : undefined,
		isDialogOpen,
		closeDialog: () => setDialogOpen(false),
		selected,
		toggleLocale: (code: string) => setSelected((prev) => toggleCode(prev, code)),
		submit,
		isRequesting: request.isPending,
		requestError: request.isError ? apiErrorMessage(request.error, 'Could not request the translation.') : null,
	}
}
